import React, { useEffect, useState } from 'react';
import { FieldProps } from '@rjsf/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * 环境选项
 */
interface EnvironmentOption {
  id: string;           // 环境标识，如 "test", "staging", "prod"
  label: string;        // 显示名称，如 "TEST 环境"
  cluster: string;      // 集群名称
  server: string;       // 集群 API Server
  namespace: string;    // 命名空间
  rawValue: string;     // 原始值（用于传递给后续步骤）
}

/**
 * SystemEnvironmentPicker
 *
 * 自定义字段：根据选中的 System 获取已配置的环境信息
 *
 * 使用方式：
 * ```yaml
 * environment:
 *   title: 部署环境
 *   type: string
 *   ui:field: SystemEnvironmentPicker
 *   ui:options:
 *     systemField: system
 * ```
 *
 * 工作流程：
 * 1. 监听 systemField 的变化
 * 2. 获取 System 实体
 * 3. 解析 hose.io/argocd-dest-{env} 注解获取环境列表
 *    - hose.io/argocd-dest-test: "clusterName|server|namespace"
 *    - hose.io/argocd-dest-staging: "clusterName|server|namespace"
 *    - hose.io/argocd-dest-prod: "clusterName|server|namespace"
 * 4. 用户选择环境后，输出环境标识（如 "test"）
 */
export const SystemEnvironmentPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
  formContext,
}: FieldProps<string>) => {
  const catalogApi = useApi(catalogApiRef);

  const [environments, setEnvironments] = useState<EnvironmentOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  // 从 uiSchema 获取 system 字段名
  const systemField = uiSchema?.['ui:options']?.systemField || 'system';
  const selectedSystem = formContext?.formData?.[systemField];

  useEffect(() => {
    if (!selectedSystem) {
      setEnvironments([]);
      return;
    }

    const loadEnvironments = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 获取 System 实体
        const systemEntity = await catalogApi.getEntityByRef(
          `system:default/${selectedSystem}`,
        );

        if (!systemEntity) {
          throw new Error(`System '${selectedSystem}' not found`);
        }

        // 解析 System 的环境注解
        const annotations = systemEntity.metadata.annotations || {};
        const envOptions: EnvironmentOption[] = [];

        // 支持的环境列表
        const envConfigs = [
          { key: 'hose.io/argocd-dest-test', id: 'test', label: 'TEST 环境' },
          { key: 'hose.io/argocd-dest-staging', id: 'staging', label: 'STAGING 环境' },
          { key: 'hose.io/argocd-dest-prod', id: 'prod', label: 'PROD 环境' },
        ];

        for (const envConfig of envConfigs) {
          const value = annotations[envConfig.key];
          if (value) {
            // 尝试解析 JSON 格式（新格式）: {"server":"...","namespace":"...","name":"...","clusterName":"..."}
            try {
              const destConfig = JSON.parse(value);
              if (destConfig && typeof destConfig === 'object') {
                envOptions.push({
                  id: envConfig.id,
                  label: envConfig.label,
                  cluster: destConfig.clusterName || '',
                  server: destConfig.server || '',
                  namespace: destConfig.namespace || '',
                  rawValue: value,
                });
                continue;
              }
            } catch {
              // 不是 JSON 格式，继续尝试旧格式
            }

            // 向后兼容：解析旧格式 "clusterName|server|namespace"
            const parts = value.split('|');
            if (parts.length >= 3) {
              envOptions.push({
                id: envConfig.id,
                label: envConfig.label,
                cluster: parts[0],
                server: parts[1],
                namespace: parts[2],
                rawValue: value,
              });
            }
          }
        }

        if (envOptions.length === 0) {
          setError(`System '${selectedSystem}' 未配置任何环境`);
        }

        setEnvironments(envOptions);

        // 如果之前选中的环境在新的列表中不存在，清空选择
        if (formData && !envOptions.find(e => e.id === formData)) {
          onChange(undefined as any);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load environments',
        );
        setEnvironments([]);
      } finally {
        setLoading(false);
      }
    };

    loadEnvironments();
  }, [selectedSystem, catalogApi, formData, onChange]);

  // 获取环境对应的颜色
  const getEnvColor = (envId: string): 'default' | 'primary' | 'secondary' => {
    switch (envId) {
      case 'test':
        return 'default';
      case 'staging':
        return 'primary';
      case 'prod':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // 如果没有选择 system，显示提示
  if (!selectedSystem) {
    return (
      <FormControl margin="normal" required={required} fullWidth disabled>
        <InputLabel htmlFor={idSchema.$id}>
          {schema.title || 'Environment'}
        </InputLabel>
        <Select id={idSchema.$id} value="">
          <MenuItem value="" disabled>
            请先选择系统
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema.$id}>
        {schema.title || 'Environment'}
      </InputLabel>
      <Select
        id={idSchema.$id}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            {error}
          </MenuItem>
        )}
        {!loading && !error && environments.length === 0 && (
          <MenuItem value="" disabled>
            该系统未配置环境（请先配置 System 环境）
          </MenuItem>
        )}
        {environments.map(env => (
          <MenuItem key={env.id} value={env.id}>
            <Box display="flex" alignItems="center" width="100%">
              <Chip
                label={env.id.toUpperCase()}
                size="small"
                color={getEnvColor(env.id)}
                style={{ marginRight: 12, minWidth: 70 }}
              />
              <Box>
                <Typography variant="body2">
                  {env.namespace} @ {env.cluster}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
      {formData && (() => {
        const selectedEnv = environments.find(e => e.id === formData);
        return selectedEnv ? (
          <FormHelperText>
            已选择: {selectedEnv.label} - {selectedEnv.cluster} 集群、{selectedEnv.namespace} namespace
          </FormHelperText>
        ) : null;
      })()}
    </FormControl>
  );
};
